import React, { ReactNode, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import cn from 'classnames';

import { useAppDispatch, useAppSelector } from 'redux-store';
import { init } from 'redux-store/slices/survey';

import SurveyHeader from "components/domains/survey/SurveyHeader";
import SurveyContent from "components/domains/survey/SurveyContent";
import SurveyProgressBar from "components/domains/survey/SurveyProgressBar";
import NextButton from "components/domains/survey/NextButton";
import BackButton from "components/domains/survey/BackButton";
import CookieBanner from 'components/complex/CookieBanner';

import styles from './styles.module.scss';

type Props = {
  children: ReactNode;
  isAvailabelHeader?: boolean;
  isAvailabelNextButton?: boolean;
  isAvailabelBackButton?: boolean;
  isAvailableProgressBar?: boolean;
  isDisabledNextButton?: boolean;
  nextButtonText?: string;
  nextButtonClassName?: string;
  footerFixed?: boolean;
  showTerms?: boolean;
  showCookieBanner?: boolean;
  backgroundColor?: string;
  theme?: string;
  onClickNextButton?: (...args: any[]) => void;
  steps?: Array<string>
};

let initSteps = [
  '/survey/v30/welcome',
  '/survey/v30/1-device',
  '/survey/v30/2-hrv-track',
  '/survey/v30/3-stress-level',
  '/survey/v30/4-physical-activity',
  '/survey/v30/5-sleep-quality',
  '/survey/v30/6-wake-up',
  '/survey/v30/7-watch-detect',
  '/survey/v30/8-lose-focus',
  '/survey/v30/9-recharge',
  '/survey/v30/10-health-concerns',
  '/survey/v30/11-looks-health-data',
  '/survey/v30/12-wish-next',
  '/survey/v30/13-age',
  '/survey/v30/14-like-to',
  '/survey/v30/best-self',
  '/survey/v30/reg',
  '/survey/v30/registration',
  '/survey/v30/products-payment'
];

/**
 * Provides simple nav layout for survey pages.
 */
export default function SurveyPage({
  children,
  isAvailabelHeader = false,
  isAvailabelNextButton = true,
  isDisabledNextButton = false,
  isAvailabelBackButton = true,
  isAvailableProgressBar = true,
  nextButtonText = 'Next',
  nextButtonClassName = styles.nextButton,
  footerFixed = false,
  showTerms = true,
  showCookieBanner = false,
  backgroundColor = '',
  theme = 'dark',
  onClickNextButton,
  steps,
}: Props) {

  const router = useRouter();

  const dispatch = useAppDispatch();
  const isInit = useAppSelector((state) => state.onboarding.isInit);
  const nextPageUrl = useAppSelector((state) => state.onboarding.nextPageUrl);
  const currentPageUrl = useAppSelector((state) => state.onboarding.currentPageUrl);

  const wrapperStyle = {} as React.CSSProperties;
  if (backgroundColor) {
    wrapperStyle.backgroundColor = backgroundColor;
  }

  useEffect(() => {
    if (isInit === true || router.isReady === false) {
      return;
    }
    
    const routeQuery = router.query;
    if (routeQuery && Object.keys(routeQuery).length > 0) {
      const queryString = new URLSearchParams(routeQuery as Record<string, string>).toString();
      initSteps = initSteps.map(stepUrl => {
        return `${stepUrl}/?${queryString}`;
      })
    }

    dispatch(init({steps: initSteps}));
  }, [isInit, router.isReady]);

  useEffect(() => {
    if (isInit) {
      router.prefetch(nextPageUrl);
    }
  }, [isInit, nextPageUrl]);

  useEffect(() => {
    if (isInit) {
      // @ts-ignore
      global.ttq?.page();
    }
  }, [router]);

  const getSurveyProgress = (currentPath: string) => {
    const start = '1-device';
    const end = '14-like-to';
  
    const startIndex = initSteps.findIndex(item => item.includes(start));
    const endIndex = initSteps.findIndex(item => item.includes(end));
  
    if (startIndex === undefined || endIndex === undefined || startIndex > endIndex) {
      return 0;
    }
  
    const progressSteps = initSteps.slice(startIndex, endIndex + 1);
    const currentIndex = progressSteps.indexOf(currentPath) + 1;
  
    if (currentIndex < startIndex) {
      return -1;
    }
    else if (currentIndex > endIndex) {
      return 110;
    }
  
    return Math.round(((currentIndex) / progressSteps.length) * 100);
  }

  const progress = getSurveyProgress(currentPageUrl);

  return (
    <div className={cn(styles.surveyWrapper, { [styles.darkTheme]: theme === 'dark' })} style={wrapperStyle}>
      <div className={styles.survey}>
        {isAvailabelNextButton && <div className={cn(styles.backgroundNextButton, styles.darkTheme)} />}
        
        {isAvailabelHeader && 
          <SurveyHeader theme={theme} />
        }
        {isAvailabelBackButton &&
          <BackButton theme={theme} />
        }
        {(isAvailableProgressBar && progress >= 0 && progress <= 100) &&
          <SurveyProgressBar theme="dark" progress={progress} />
        }
        <SurveyContent className={cn(styles.content, { [styles.footerFixed]: footerFixed })} theme={theme}>{children}</SurveyContent>
        {isAvailabelNextButton &&
          <div className={cn(styles.footer, { [styles.footerFixed]: footerFixed })}>
            <NextButton
              text={nextButtonText}
              className={nextButtonClassName}
              theme={theme}
              disabled={isDisabledNextButton}
              onClick={onClickNextButton}
            />
          </div>
        }

        {showTerms &&
          <div className={cn(styles.terms, styles.darkTheme)}>
            <Link href="https://welltory.com/terms/" passHref>
              {/* eslint-disable-next-line react/jsx-max-depth */}
              <span className={styles.link}>Terms of Use</span>
            </Link>
            {' • '}
            <Link href="https://welltory.com/privacy/" passHref>
              {/* eslint-disable-next-line react/jsx-max-depth */}
              <span className={styles.link}>Privacy Policy </span>
            </Link>
            {' • '}
            <Link href="https://welltory.com/cookie/" passHref>
              {/* eslint-disable-next-line react/jsx-max-depth */}
              <span className={styles.link}>Cookie Policy </span>
            </Link>
          </div>
        }

      </div>
      {showCookieBanner &&
        <CookieBanner className={styles.cookieBanner} />
      }
    </div>
  );
}